'use client'

import { useEffect, useMemo, useState } from 'react'

// MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

// API Imports
import { personasApi } from '@/lib/api'

// Context Imports
import { useAuth } from '@/contexts/auth-context'

type StatCardProps = {
  label: string
  value: string
}

const StatCard = ({ label, value }: StatCardProps) => {
  return (
    <Card variant='outlined' sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Box display='flex' flexDirection='column' gap={0.5}>
          <Typography variant='caption' color='text.secondary'>
            {label}
          </Typography>
          <Typography variant='h5' fontWeight={700}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

const DashboardGIA = () => {
  const { centroDefault, permissions, isLoading: isAuthLoading } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [trabajadoresCount, setTrabajadoresCount] = useState(0)
  const [atendidosCount, setAtendidosCount] = useState(0)

  const isSuperuser = useMemo(() => permissions.includes('*'), [permissions])
  const centroId = useMemo(() => (isSuperuser ? undefined : centroDefault?.id), [isSuperuser, centroDefault?.id])

  useEffect(() => {
    const loadStats = async () => {
      if (isAuthLoading) return

      setIsLoading(true)

      try {
        const [trabajadores, atendidos] = await Promise.all([
          personasApi.list({ page: 1, tipo: 'trabajador', centro: centroId }),
          personasApi.list({ page: 1, tipo: 'atendido', centro: centroId })
        ])

        setTrabajadoresCount(trabajadores.count ?? 0)
        setAtendidosCount(atendidos.count ?? 0)
      } catch (error) {
        console.error(error)
        setTrabajadoresCount(0)
        setAtendidosCount(0)
      } finally {
        setIsLoading(false)
      }
    }

    loadStats()
  }, [centroId, isAuthLoading])

  const centroLabel = centroDefault?.nombre ? centroDefault.nombre : centroId ? `Centro ${centroId}` : 'Global'

  return (
    <Box display='flex' flexDirection='column' gap={3}>
      <Box>
        <Typography variant='h5'>Dashboard</Typography>
        <Typography variant='body2' color='text.secondary'>
          Resumen compacto - {centroLabel}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <StatCard label='Trabajadores' value={isLoading ? '...' : trabajadoresCount.toString()} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <StatCard label='Atendidos' value={isLoading ? '...' : atendidosCount.toString()} />
        </Grid>
      </Grid>
    </Box>
  )
}

export default DashboardGIA
